import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, CheckCircle2, Globe, Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useConnectWorkerCms,
  useCreateSite,
  useDiscoverWorkerCms,
  useTestCredentials,
  useTestWorkerCms,
  type RemoteWorkerCmsSite,
} from '@/hooks/useSites';
import { SeoSnippetHelper } from '@/components/sites/SeoSnippetHelper';
import { HtaccessHelper } from '@/components/sites/HtaccessHelper';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

type SeoPlugin = 'none' | 'yoast' | 'rankmath';
type PostStatus = 'publish' | 'draft' | 'pending';
type Platform = 'wordpress' | 'workercms';

export function AddSitePage() {
  const navigate = useNavigate();
  const create = useCreateSite();
  const testCreds = useTestCredentials();
  const testWcms = useTestWorkerCms();
  const discoverWcms = useDiscoverWorkerCms();
  const connectWcms = useConnectWorkerCms();
  const [platform, setPlatform] = useState<Platform>('wordpress');

  // WorkerCms-specific state
  const [wcmsBaseUrl, setWcmsBaseUrl] = useState('https://workercms.com');
  const [wcmsApiKey, setWcmsApiKey] = useState('');
  const [wcmsTest, setWcmsTest] = useState<{ ok: boolean; message: string } | null>(null);
  const [wcmsDiscovered, setWcmsDiscovered] = useState<RemoteWorkerCmsSite[] | null>(null);
  const [wcmsError, setWcmsError] = useState<string | null>(null);
  const [wcmsConnected, setWcmsConnected] = useState<{
    addedCount: number;
    skippedDup: number;
    skippedLimit: number;
    addedNames: string[];
  } | null>(null);

  const [name, setName] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [seoPlugin, setSeoPlugin] = useState<SeoPlugin>('none');
  const [defaultStatus, setDefaultStatus] = useState<PostStatus>('publish');
  const [woocommerce, setWoocommerce] = useState(false);
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    message: string;
    details?: { hint?: string; status?: number };
  } | null>(null);

  const onTest = async () => {
    setError(null);
    setTestResult(null);
    try {
      const res = await testCreds.mutateAsync({
        site_url: siteUrl,
        username,
        app_password: appPassword,
      });
      setTestResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bağlantı testi başarısız');
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const site = await create.mutateAsync({
        name: name.trim() || siteUrl.replace(/^https?:\/\//, ''),
        site_url: siteUrl.trim(),
        username: username.trim(),
        app_password: appPassword.trim(),
        seo_plugin: seoPlugin,
        default_status: defaultStatus,
        woocommerce,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        notes: notes.trim() || undefined,
      });
      navigate({ to: '/sites' });
      void site;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Site eklenirken hata oluştu');
    }
  };

  const onWcmsTest = async () => {
    setWcmsError(null);
    setWcmsTest(null);
    try {
      const res = await testWcms.mutateAsync({ base_url: wcmsBaseUrl, api_key: wcmsApiKey });
      setWcmsTest(res);
    } catch (err) {
      setWcmsError(err instanceof ApiError ? err.message : 'Bağlantı testi başarısız');
    }
  };

  const onWcmsDiscover = async () => {
    setWcmsError(null);
    setWcmsDiscovered(null);
    try {
      const res = await discoverWcms.mutateAsync({ base_url: wcmsBaseUrl, api_key: wcmsApiKey });
      setWcmsDiscovered(res.sites);
    } catch (err) {
      setWcmsError(err instanceof ApiError ? err.message : 'Site listesi alınamadı');
    }
  };

  const onWcmsConnect = async () => {
    setWcmsError(null);
    setWcmsConnected(null);
    try {
      const res = await connectWcms.mutateAsync({ base_url: wcmsBaseUrl, api_key: wcmsApiKey });
      setWcmsConnected({
        addedCount: res.added.length,
        skippedDup: res.skipped.filter((s) => s.reason === 'already_added').length,
        skippedLimit: res.skipped.filter((s) => s.reason === 'site_limit_reached').length,
        addedNames: res.added.map((s) => s.name),
      });
    } catch (err) {
      setWcmsError(err instanceof ApiError ? err.message : 'Bağlanırken hata oluştu');
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/sites">
            <ArrowLeft className="w-4 h-4" />
            <span>Sitelere dön</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold mt-2">Yeni Site Ekle</h1>
        <p className="text-muted-foreground text-sm">
          {platform === 'wordpress'
            ? 'WordPress sitenizi App Password ile bağlayın.'
            : 'WorkerCms hesabınızdaki tüm siteleri tek anahtarla otomatik içe aktarın.'}
        </p>
      </div>

      {/* Platform picker */}
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setPlatform('wordpress')}
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4 text-left transition',
            platform === 'wordpress'
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-foreground/30',
          )}
        >
          <Globe className="w-5 h-5 mt-0.5 text-muted-foreground" />
          <div>
            <div className="font-medium">WordPress</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Tek site, App Password ile bağla
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setPlatform('workercms')}
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4 text-left transition',
            platform === 'workercms'
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-foreground/30',
          )}
        >
          <Sparkles className="w-5 h-5 mt-0.5 text-muted-foreground" />
          <div>
            <div className="font-medium">WorkerCms</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Tek anahtar, tüm siteleri otomatik keşfet
            </div>
          </div>
        </button>
      </div>

      {/* WorkerCms flow */}
      {platform === 'workercms' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">WorkerCms Bağlantısı</CardTitle>
            <CardDescription>
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">workercms.com → API Anahtarları → Hesabım</code>
              {' '}sayfasından <strong>wcms_</strong> ile başlayan bir anahtar oluştur, buraya yapıştır.
              Erişebildiğin tüm siteleri otomatik içe aktaracağız.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wcms_base">WorkerCms Base URL</Label>
              <Input
                id="wcms_base"
                type="url"
                value={wcmsBaseUrl}
                onChange={(e) => setWcmsBaseUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Çoğu kullanıcı için <code>https://workercms.com</code> doğrudur.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wcms_key">API Anahtarı</Label>
              <Input
                id="wcms_key"
                type="password"
                placeholder="wcms_..."
                value={wcmsApiKey}
                onChange={(e) => setWcmsApiKey(e.target.value)}
                className="font-mono"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={onWcmsTest}
                disabled={testWcms.isPending || !wcmsBaseUrl || !wcmsApiKey}
              >
                {testWcms.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Bağlantı Testi
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onWcmsDiscover}
                disabled={discoverWcms.isPending || !wcmsBaseUrl || !wcmsApiKey}
              >
                {discoverWcms.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                Siteleri Listele
              </Button>
              <Button
                type="button"
                onClick={onWcmsConnect}
                disabled={connectWcms.isPending || !wcmsBaseUrl || !wcmsApiKey}
              >
                {connectWcms.isPending ? 'İçe Aktarılıyor…' : 'Bağla ve Tümünü İçe Aktar'}
              </Button>
            </div>

            {wcmsTest && (
              <div
                className={cn(
                  'flex items-start gap-2 text-sm rounded-md border px-3 py-2',
                  wcmsTest.ok
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                    : 'border-destructive/30 bg-destructive/10 text-destructive',
                )}
              >
                {wcmsTest.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
                <span>{wcmsTest.message}</span>
              </div>
            )}

            {wcmsDiscovered && (
              <div className="rounded-md border bg-muted/30 p-3">
                <div className="text-sm font-medium mb-2">{wcmsDiscovered.length} site bulundu:</div>
                <ul className="text-sm space-y-1">
                  {wcmsDiscovered.map((s) => (
                    <li key={s.id} className="flex items-center gap-2">
                      <Globe className="w-3 h-3 text-muted-foreground" />
                      <span className="font-medium">{s.name}</span>
                      {s.primary_domain && (
                        <span className="text-xs text-muted-foreground">— {s.primary_domain}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {wcmsError && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                {wcmsError}
              </div>
            )}

            {wcmsConnected && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                <div className="flex items-center gap-2 font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  {wcmsConnected.addedCount} site eklendi
                  {wcmsConnected.skippedDup > 0 && ` · ${wcmsConnected.skippedDup} zaten ekliydi`}
                  {wcmsConnected.skippedLimit > 0 && ` · ${wcmsConnected.skippedLimit} site limiti nedeniyle atlandı`}
                </div>
                {wcmsConnected.addedNames.length > 0 && (
                  <ul className="mt-2 ml-6 list-disc text-xs">
                    {wcmsConnected.addedNames.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => navigate({ to: '/sites' })}>
                    Sitelere Git
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setWcmsConnected(null);
                      setWcmsApiKey('');
                      setWcmsDiscovered(null);
                    }}
                  >
                    Başka Bağlantı Ekle
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {platform === 'wordpress' && (
      <form onSubmit={onSubmit} className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Site Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="site_url">
                Site URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="site_url"
                type="url"
                placeholder="https://ornek.com"
                required
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                https:// ile başlasın, sonuna slash eklemeyin.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Görünen Ad</Label>
              <Input
                id="name"
                placeholder="Boş bırakırsan URL otomatik kullanılır"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="username">
                  Kullanıcı Adı <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="username"
                  required
                  placeholder="admin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  <em>yönetici</em> veya <em>editör</em> rolü olmalı.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_password">
                  Application Password <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="app_password"
                  type="password"
                  required
                  placeholder="xxxx xxxx xxxx xxxx"
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                />
              </div>
            </div>

            {/* Test connection */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onTest}
                  disabled={testCreds.isPending || !siteUrl || !username || !appPassword}
                >
                  {testCreds.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4" />
                  )}
                  Bağlantıyı Test Et
                </Button>
                {testResult && (
                  <div
                    className={cn(
                      'flex items-start gap-2 text-sm rounded-md border px-3 py-2 flex-1 min-w-[200px]',
                      testResult.ok
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
                        : 'border-destructive/30 bg-destructive/10 text-destructive',
                    )}
                  >
                    {testResult.ok ? (
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    )}
                    <span>{testResult.message}</span>
                  </div>
                )}
              </div>
              {testResult?.details?.hint === 'auth_header_stripped' && <HtaccessHelper />}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Yayın Ayarları</CardTitle>
            <CardDescription>İçerikler bu sitede nasıl yayınlanacak?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="status">Varsayılan Durum</Label>
                <select
                  id="status"
                  value={defaultStatus}
                  onChange={(e) => setDefaultStatus(e.target.value as PostStatus)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="publish">Yayınla</option>
                  <option value="draft">Taslak</option>
                  <option value="pending">Onay bekliyor</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="seo">SEO Eklentisi</Label>
                <select
                  id="seo"
                  value={seoPlugin}
                  onChange={(e) => setSeoPlugin(e.target.value as SeoPlugin)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="none">Yok</option>
                  <option value="yoast">Yoast SEO</option>
                  <option value="rankmath">Rank Math SEO</option>
                </select>
              </div>
            </div>

            {seoPlugin !== 'none' && (
              <div className="rounded-md border bg-muted/30 p-3">
                <SeoSnippetHelper plugin={seoPlugin} />
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                id="woo"
                type="checkbox"
                checked={woocommerce}
                onChange={(e) => setWoocommerce(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="woo" className="cursor-pointer">
                WooCommerce kullanıyor
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Etiketler (virgülle ayırarak)</Label>
              <Input
                id="tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="haber, teknoloji, blog"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notlar (opsiyonel)</Label>
              <textarea
                id="notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Ekleniyor…' : 'Siteyi Ekle'}
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to="/sites">İptal</Link>
          </Button>
        </div>
      </form>
      )}
    </div>
  );
}
